import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import notify from 'devextreme/ui/notify';
import DataGrid, {
  Column, Paging, Sorting,
} from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import { LoadPanel } from 'devextreme-react/load-panel';

import { useStaffListApiService } from '../../services';
import { appConst } from '../../app-const';
import { showError } from '../../helpers/ui';
import './committee-documents-grid.scss';

// Documents are stored without a content type, so the download response is always
// application/octet-stream. Infer a renderable MIME type from the file extension
// so the browser can display the file inline instead of downloading it.
const mimeTypeFromFileName = (fileName) => {
  const extension = fileName?.split('.').pop()?.toLowerCase();
  return appConst.mimeTypesByExtension[extension];
};

// Human-readable file size from a byte count (blank for folders).
const formatSize = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

// Read-only grid view of a committee's documents. Folders are rows that navigate
// (via a breadcrumb); files expose View (inline preview) and Download actions.
// All HTTP lives in the StaffList API service.
export default function CommitteeDocumentsGrid({ committeeId }) {
  const { listCommitteeDocumentsAsync, downloadCommitteeDocumentAsync } = useStaffListApiService();
  // Current folder relative to the committee root ('' = root).
  const [currentPath, setCurrentPath] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (path) => {
    setLoading(true);
    try {
      const { data } = await listCommitteeDocumentsAsync(committeeId, path);
      setItems(data ?? []);
    } catch (error) {
      showError(error.message);
    } finally {
      setLoading(false);
    }
  }, [committeeId, listCommitteeDocumentsAsync]);

  useEffect(() => { load(currentPath); }, [load, currentPath]);

  const openFolder = useCallback((fileName) => {
    setCurrentPath((prev) => (prev ? `${prev}/${fileName}` : fileName));
  }, []);

  // Opens the document inline in a new tab via a blob: URL.
  const viewDocument = useCallback(async (blobPath) => {
    try {
      const rawBlob = await downloadCommitteeDocumentAsync(committeeId, blobPath);
      const contentType = mimeTypeFromFileName(blobPath) || rawBlob.type || 'application/octet-stream';
      const objectUrl = window.URL.createObjectURL(new Blob([rawBlob], { type: contentType }));
      window.open(objectUrl, '_blank', 'noopener noreferrer');
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
    } catch (error) {
      notify({ message: error.message, shading: true }, 'error');
    }
  }, [committeeId, downloadCommitteeDocumentAsync]);

  // Saves the document to disk (rather than opening it inline).
  const downloadDocument = useCallback(async (blobPath, fileName) => {
    try {
      const rawBlob = await downloadCommitteeDocumentAsync(committeeId, blobPath);
      const objectUrl = window.URL.createObjectURL(rawBlob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
    } catch (error) {
      notify({ message: error.message, shading: true }, 'error');
    }
  }, [committeeId, downloadCommitteeDocumentAsync]);

  // Breadcrumb segments: Home + each folder in the current path.
  const crumbs = useMemo(() => {
    const segments = currentPath ? currentPath.split('/') : [];
    return [
      { label: 'Home', path: '' },
      ...segments.map((seg, i) => ({ label: seg, path: segments.slice(0, i + 1).join('/') })),
    ];
  }, [currentPath]);

  const renderName = useCallback((cell) => {
    const item = cell.data;
    if (item.isDirectory) {
      return (
        <span className="doc-link" onClick={() => openFolder(item.fileName)}>
          <i className="dx-icon-folder" />
          {' '}
          {item.fileName}
        </span>
      );
    }
    return (
      <span className="doc-link" onClick={() => viewDocument(item.name)}>{item.fileName}</span>
    );
  }, [openFolder, viewDocument]);

  const renderActions = useCallback((cell) => {
    const item = cell.data;
    if (item.isDirectory) {
      return <Button icon="folder" hint="Open" stylingMode="text" onClick={() => openFolder(item.fileName)} />;
    }
    return (
      <div className="doc-actions">
        <Button icon="eyeopen" hint="View" stylingMode="text" onClick={() => viewDocument(item.name)} />
        <Button icon="download" hint="Download" stylingMode="text" onClick={() => downloadDocument(item.name, item.fileName)} />
      </div>
    );
  }, [openFolder, viewDocument, downloadDocument]);

  return (
    <div className="committee-documents-grid">
      <div className="doc-breadcrumb">
        {crumbs.map((crumb, i) => (
          <span key={crumb.path || 'home'}>
            {i > 0 && <span className="sep"> / </span>}
            {i < crumbs.length - 1
              ? <span className="crumb-link" onClick={() => setCurrentPath(crumb.path)}>{crumb.label}</span>
              : <span className="crumb-current">{crumb.label}</span>}
          </span>
        ))}
      </div>

      <DataGrid
        dataSource={items}
        keyExpr="id"
        showBorders
        columnAutoWidth
        rowAlternationEnabled
        hoverStateEnabled
        wordWrapEnabled
        width="100%"
        noDataText="No documents."
        columnHidingEnabled
      >
        <Sorting mode="none" />
        <Paging enabled={false} />
        {/* hidingPriority: lower numbers collapse first into the adaptive detail
            row on small screens. Name and Actions (highest) are kept longest. */}
        <Column caption="Name" dataField="fileName" cellRender={renderName} hidingPriority={4} minWidth={120} />
        <Column caption="Date Uploaded" dataField="dateUploaded" dataType="datetime" format={appConst.dateAndTimeDisplayFormat} hidingPriority={0} />
        <Column caption="Size" dataField="size" alignment="right" cellRender={(c) => formatSize(c.data.size)} hidingPriority={1} />
        <Column caption="Uploaded By" dataField="uploadedBy" hidingPriority={2} />
        <Column caption="" width={100} alignment="center" cellRender={renderActions} hidingPriority={3} />
      </DataGrid>

      <LoadPanel visible={loading} shading={false} />
    </div>
  );
}
